import React, { useState } from "react";
import { toast } from "sonner";
import { Mail, Send } from "lucide-react";
import client, { formatApiError } from "../lib/api";
import Layout, { PageHero } from "../components/site/Layout";
import { TRUST } from "../lib/data";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", mobileNumber: "", subject: "", message: "", website: "" });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await client.post("/contact", form);
      toast.success("Your message has been received. Thank you for contacting us.");
      setForm({ name: "", email: "", mobileNumber: "", subject: "", message: "", website: "" });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setBusy(false); }
  };

  return (
    <Layout>
      <PageHero eyebrow="Contact Us" title="We would be glad to hear from you." intro="For enquiries, please write to us using the details or the form below." />
      <section className="mx-auto grid max-w-7xl gap-12 px-5 py-20 lg:grid-cols-12 lg:gap-16 lg:px-10 lg:py-24">
        <div className="lg:col-span-5">
          <h2 className="font-serif text-2xl text-maroon">{TRUST.name}</h2>
          <address className="mt-5 not-italic text-base leading-relaxed text-ink-muted">
            {TRUST.addressLines.map((l) => <div key={l}>{l}</div>)}
          </address>
          <a href={`mailto:${TRUST.email}`} className="mt-6 inline-flex items-center gap-2 text-maroon hover:text-gold-dark" data-testid="contact-email">
            <Mail size={17} /> {TRUST.email}
          </a>
        </div>

        <div className="lg:col-span-7">
          <form onSubmit={submit} className="space-y-5 border border-gold/25 bg-white p-8" data-testid="contact-form">
            <input type="text" name="website" value={form.website} onChange={set("website")} className="hidden" tabIndex={-1} autoComplete="off" aria-hidden="true" />
            <div className="grid gap-5 sm:grid-cols-2">
              <div><Label htmlFor="c-name">Full Name *</Label><Input id="c-name" required value={form.name} onChange={set("name")} data-testid="contact-name" className="mt-2" /></div>
              <div><Label htmlFor="c-email">Email Address *</Label><Input id="c-email" type="email" required value={form.email} onChange={set("email")} data-testid="contact-email-input" className="mt-2" /></div>
            </div>
            <div className="grid gap-5 sm:grid-cols-2">
              <div><Label htmlFor="c-mobile">Mobile Number</Label><Input id="c-mobile" value={form.mobileNumber} onChange={set("mobileNumber")} data-testid="contact-mobile" className="mt-2" /></div>
              <div><Label htmlFor="c-subject">Subject</Label><Input id="c-subject" value={form.subject} onChange={set("subject")} data-testid="contact-subject" className="mt-2" /></div>
            </div>
            <div><Label htmlFor="c-message">Message *</Label><Textarea id="c-message" required rows={5} value={form.message} onChange={set("message")} data-testid="contact-message" className="mt-2" /></div>
            <button type="submit" disabled={busy} data-testid="contact-submit" className="inline-flex items-center gap-2 bg-maroon px-7 py-3.5 text-xs uppercase tracking-wide text-ivory transition-colors duration-300 hover:bg-maroon-dark disabled:opacity-60">
              {busy ? "Sending..." : <>Send Message <Send size={15} /></>}
            </button>
          </form>
        </div>
      </section>
    </Layout>
  );
}
